import { useTexture } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useRef } from "react";
import { Group, LinearFilter, LinearMipmapLinearFilter, Object3D, SpotLight, SRGBColorSpace } from "three";
import { homeHeroMedia } from "../../content/siteContent";
import { getViewportTier, isMobileViewport } from "../../lib/viewport";
import { getStudioImageLayout } from "./studioSceneLayout";

type StudioSceneProps = {
  weight: number;
  reducedMotion: boolean;
  transitionProgress: number;
  transitioningToProjects: boolean;
};

export function StudioScene({ reducedMotion, transitioningToProjects, transitionProgress, weight }: StudioSceneProps) {
  const viewport = useThree((state) => getViewportTier(state.size.width));
  const mobile = isMobileViewport(viewport);
  const imageLayout = getStudioImageLayout(viewport);
  const groupRef = useRef<Group>(null);
  const texture = useTexture(homeHeroMedia.src);
  const imageRef = useRef<Group>(null);
  const imageLightRef = useRef<SpotLight>(null);
  const imageLightTargetRef = useRef<Object3D>(null);
  const atmosphereOpacity = mobile ? 0.28 : 0;

  useEffect(() => {
    texture.colorSpace = SRGBColorSpace;
    texture.generateMipmaps = true;
    texture.minFilter = LinearMipmapLinearFilter;
    texture.magFilter = LinearFilter;
    texture.needsUpdate = true;
  }, [texture]);

  useEffect(() => {
    if (!imageLightRef.current || !imageLightTargetRef.current) {
      return;
    }

    imageLightRef.current.target = imageLightTargetRef.current;
    imageLightRef.current.target.updateMatrixWorld();
  }, []);

  useFrame((state, delta) => {
    if (!groupRef.current || !imageRef.current) {
      return;
    }

    const shouldFloat = !mobile && !reducedMotion;

    const drift = shouldFloat ? Math.sin(state.clock.elapsedTime * 0.22) * 0.08 : 0;
    const rotationDrift = shouldFloat ? Math.sin(state.clock.elapsedTime * 0.18) * 0.05 : 0;

    groupRef.current.position.y +=  (drift - groupRef.current.position.y) * (1 - Math.exp(-delta * 2.6));

    groupRef.current.rotation.y +=
      (rotationDrift - groupRef.current.rotation.y) * (1 - Math.exp(-delta * 2.8));

    if (!transitioningToProjects) {
      const [restX, restY, restZ] = imageLayout.position;
      imageRef.current.position.x += (restX - imageRef.current.position.x) * (1 - Math.exp(-delta * 3.2));
      imageRef.current.position.y += (restY - imageRef.current.position.y) * (1 - Math.exp(-delta * 3.2));
      imageRef.current.position.z += (restZ - imageRef.current.position.z) * (1 - Math.exp(-delta * 3.2));
      return;
    }

    const softenProgress = Math.min(Math.max((transitionProgress - 0.18) / 0.34, 0), 1);
    const disperseX = mobile ? imageLayout.position[0] : 2.2;
    const disperseZ = mobile ? -0.5 : -0.45;
    imageRef.current.position.x += (disperseX - imageRef.current.position.x) * softenProgress * (1 - Math.exp(-delta * 4.4));
    imageRef.current.position.z += (disperseZ - imageRef.current.position.z) * softenProgress * (1 - Math.exp(-delta * 4.4));
  });

  return (
    <group ref={groupRef} visible={weight > 0.001}>
      <ambientLight intensity={0.16 * weight} />
      <directionalLight color="#d9d6d4" intensity={0.18 * weight} position={[-2.8, 3.4, 2.6]} />
      <group ref={imageRef} position={imageLayout.position}>
        <object3D ref={imageLightTargetRef} position={[0.3, 0.02, -0.4]} />
        <mesh castShadow
          receiveShadow scale={imageLayout.scale}>

          <boxGeometry args={[1, 1, 0.045]} />
          <meshStandardMaterial
            emissive="#ffffff"
            emissiveIntensity={0.028 * weight}
            emissiveMap={texture}
            map={texture}
            transparent
          />
        </mesh>
      </group>
    </group>
  );
}
